<script setup lang="ts">
import { computed, defineProps, ref } from 'vue';
import { Dependency, TableLabels } from '../types';

const props = defineProps<{
  dependencies: Dependency[];
  tableLabels: TableLabels;
}>();

const filteredDependencies = computed(() =>
  props.dependencies.filter((dependency) => dependency.license),
);

const activeDependency = ref<Dependency | null>(null);
const isModalOpen = ref(false);

const triggerModal = () => {
  if (isModalOpen.value) {
    isModalOpen.value = false;
    activeDependency.value = null;
  } else {
    isModalOpen.value = true;
  }
};
</script>

<template>
  <div class="wbx-grid-row" v-if="filteredDependencies.length">
    <div class="wbx-grid-col-mq1-12">
      <table class="wbx-table wbx-table--striped dependencies-table">
        <thead>
          <tr>
            <th scope="col">
              <p class="wb-text-m-strong">{{ props.tableLabels.dependency }}</p>
            </th>
            <th scope="col">
              <p class="wb-text-m-strong">{{ props.tableLabels.version }}</p>
            </th>
            <th scope="col">
              <p class="wb-text-m-strong">{{ props.tableLabels.license }}</p>
            </th>
            <th scope="col">
              <p class="wb-text-m-strong">{{ props.tableLabels.url }}</p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(dependency, key) in filteredDependencies" :key="key">
            <td>
              <code class="wb-text-m-strong">{{ dependency.name }}</code>
            </td>
            <td>
              <p class="wb-text-m">{{ dependency.version }}</p>
            </td>
            <td>
              <a
                href="#"
                class="wbx-link wbx-link--inline wb-text-m"
                @click.prevent="
                  () => {
                    activeDependency = dependency;
                    triggerModal();
                  }
                "
                @keydown.enter.prevent="
                  () => {
                    activeDependency = dependency;
                    triggerModal();
                  }
                "
                @keydown.space.prevent="
                  () => {
                    activeDependency = dependency;
                    triggerModal();
                  }
                "
                role="button"
                >{{ dependency.license }}</a
              >
            </td>
            <td>
              <a
                :href="dependency.homepage ?? dependency.repository?.url"
                class="wbx-link wbx-link--inline wb-text-m"
                target="_blank"
              >
                {{ props.tableLabels.link }}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else>
    <p class="wb-text-m">Nothing to see here</p>
  </div>
  <wb7-modal
    variant="info"
    size="l"
    close-on-backdrop-click="true"
    close-on-escape-key="false"
    v-show="isModalOpen"
    @wbclose="triggerModal"
    ref="modal"
    modal-aria-label="License information"
  >
    <wb7-modal-level
      close-button-label="Close"
      :headline="activeDependency?.license"
    >
      <p>{{ activeDependency?.licenseText }}</p>
    </wb7-modal-level>
  </wb7-modal>
</template>

<style lang="scss" scoped>
.dependencies-table {
  width: 100%;
}
</style>
